import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_family-athletics/artifacts/uy0gl72p_New%20Offcial%20Logo.jpg"
              alt="Dolphins FC Logo"
              className="h-24 mx-auto mb-4"
            />
            <h1 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight" data-testid="forgot-password-title">
              Forgot Password
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              Enter the email you use to sign in and we&apos;ll send you a reset link.
            </p>
          </div>

          {sent ? (
            <div className="space-y-4" data-testid="forgot-password-sent">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                If an account exists for <span className="font-semibold">{email}</span>, a password reset link has been sent. Please check your inbox — the link expires in 1 hour.
              </div>
              <Link to="/login" className="block text-center text-secondary hover:underline font-semibold" data-testid="forgot-back-to-login">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  data-testid="forgot-password-email-input"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit-button"
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 rounded-md transition-all duration-200"
              >
                {loading ? 'Sending reset link…' : 'Send reset link'}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-slate-600 hover:text-primary" data-testid="forgot-cancel-link">
                  Cancel and return to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
